// =====================================================================
//  LOTES DE SUBASTA (art. 148) — RUTA CRÍTICA
//
//  El art. 148 obliga a rematar en un solo lote todas las concesiones
//  comprendidas en el mismo número del rol, y el postor debe acompañar
//  boleta de depósito por el TOTAL adeudado de todo el lote.
//
//  Esta vista tiene que estar lista antes de que salga la publicación en
//  el Diario Oficial, porque ahí se conocen los lotes definitivos y si la
//  mina que interesa viene amarrada a otras tres, el mínimo y la garantía
//  se multiplican.
//
//  Sobre el prototipo agrega dos cosas:
//    · asignación real de lotes, con marca de confirmado
//    · las agrupaciones POSIBLES por número de rol repetido (PLAN §5.7)
// =====================================================================

import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useAlertas,
  useAsignarLote,
  useCausa,
  useConcesiones,
  useConfirmarLote,
  useCrearLote,
  useLotes,
  useLotesDefinidos,
} from '../datos/consultas'
import { Alerta, Cargando, ErrorCarga, Tag, TituloCard, useAvisar } from '../componentes/ui'
import { Pagina } from '../App'
import { fmt, num, plural } from '../dominio/formato'
import { garantiaPorLote } from '../dominio/calculo'
import type { Concesion, RolUsuario } from '../tipos/dominio'

export function Lotes({ rol }: { rol: RolUsuario }) {
  const concesiones = useConcesiones()
  const lotes = useLotes()
  const definidos = useLotesDefinidos()
  const alertas = useAlertas()
  const causa = useCausa()
  const crear = useCrearLote()
  const asignar = useAsignarLote()
  const confirmar = useConfirmarLote()
  const avisar = useAvisar()
  const [, setParams] = useSearchParams()

  const [nuevoCodigo, setNuevoCodigo] = useState('')
  const esMaster = rol === 'MASTER'

  const grupos = useMemo(() => agrupar(concesiones.data ?? []), [concesiones.data])
  const garantia = useMemo(() => garantiaPorLote(concesiones.data ?? []), [concesiones.data])

  const sugeridos = useMemo(() => {
    const ids = new Set(
      (alertas.data ?? []).filter((a) => a.alerta === 'POSIBLE_LOTE').map((a) => a.concesion_id),
    )
    const porRol = new Map<string, Concesion[]>()
    for (const c of concesiones.data ?? []) {
      if (!ids.has(c.id) || !c.rol_numero) continue
      const clave = `${c.comuna}|${c.rol_numero}`
      const g = porRol.get(clave)
      if (g) g.push(c)
      else porRol.set(clave, [c])
    }
    return [...porRol.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [alertas.data, concesiones.data])

  if (concesiones.isError) return <ErrorCarga error={concesiones.error} />
  if (concesiones.isLoading || !concesiones.data) return <Cargando />

  const causaId = causa.data?.id

  // El catálogo de lotes, con los totales de v_lote pegados cuando ya
  // tienen concesiones. Un lote recién creado aparece con 0 y $0 en vez de
  // desaparecer, que es lo que hacía antes y daba la impresión de que la
  // creación había fallado.
  const catalogo = (definidos.data ?? []).map((l) => {
    const agregado = (lotes.data ?? []).find((x) => x.lote_id === l.id)
    return {
      ...l,
      concesiones: agregado ? Number(agregado.concesiones) : 0,
      minimo: num(agregado?.minimo_lote),
      garantia: num(agregado?.garantia_exigida),
      marcadas: agregado ? Number(agregado.marcadas) : 0,
    }
  })

  const crearLote = async () => {
    if (!nuevoCodigo.trim() || !causaId) return
    try {
      await crear.mutateAsync({ causaId, codigo: nuevoCodigo.trim() })
      setNuevoCodigo('')
    } catch (e) {
      avisar(e)
    }
  }

  const armarSugerido = async (etiqueta: string, cs: Concesion[]) => {
    if (!causaId) return
    const codigo = `Rol ${etiqueta.split('|')[1]} · ${cs[0].comuna}`
    try {
      const [creado] = await crear.mutateAsync({
        causaId,
        codigo,
        descripcion: `Sugerido por número de rol repetido: ${cs.map((c) => c.nombre).join(' + ')}`,
      })
      for (const c of cs) {
        await asignar.mutateAsync({ id: c.id, loteId: (creado as { id: number }).id })
      }
    } catch (e) {
      avisar(e)
    }
  }

  return (
    <>
      <Pagina
        titulo="Lotes de subasta"
        sub="El artículo 148 obliga a rematar en un solo lote todas las concesiones comprendidas en el mismo número del rol, y el postor debe acompañar boleta de depósito por el total adeudado de todo el lote."
      />

      <Alerta tono="warn">
        <b>Decisión pendiente.</b> Los lotes definitivos los fija el tribunal y se conocerán con la
        publicación en el Diario Oficial. La nómina trae varias columnas de rol —fojas, referencia,
        número, tipo y año— y cuál de ellas determina el lote solo quedará claro entonces. Hasta ahí,
        toda agrupación es una hipótesis: márcala como confirmada recién cuando salga la publicación.
      </Alerta>

      {garantia.lotes - garantia.soloMarcadas > 0 ? (
        <Alerta tono="bad">
          <b>La garantía a rendir es {fmt(garantia.lotes)}, no {fmt(garantia.soloMarcadas)}.</b> Las
          concesiones marcadas están amarradas a{' '}
          {garantia.lotesTocados === 1
            ? 'un lote que incluye otras que no están marcadas'
            : `${garantia.lotesTocados} lotes que incluyen otras que no están marcadas`}
          , y el art. 148 exige boleta por el total adeudado de todo el lote. Son{' '}
          <b>{fmt(garantia.lotes - garantia.soloMarcadas)}</b> más de caja que hay que tener
          disponible el día del remate. No se descuenta del presupuesto: es caja aparte.
        </Alerta>
      ) : null}

      {/* --------- Sugerencias por número de rol repetido (PLAN §5.7) --------- */}
      {sugeridos.length > 0 ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <TituloCard>Posibles lotes · por número de rol repetido</TituloCard>
          <p className="sub" style={{ marginBottom: 12 }}>
            Estas concesiones comparten el número de rol con otra de su misma comuna y hoy figuran
            sueltas. Si el art. 148 se resuelve por esa columna, ya vienen amarradas. No es una
            afirmación — es una pista concreta para tener en el radar antes de septiembre.
          </p>
          <table>
            <thead>
              <tr>
                <th>Rol · comuna</th>
                <th>Concesiones</th>
                <th className="num">Mínimo del grupo</th>
                {esMaster ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {sugeridos.map(([clave, cs]) => (
                <tr key={clave}>
                  <td className="mono">{clave.replace('|', ' · ')}</td>
                  <td>
                    {cs.map((c) => (
                      <div key={c.id} className="nm" style={{ fontSize: 12.5 }}>
                        {c.nombre}
                      </div>
                    ))}
                  </td>
                  <td className="num">
                    <b>{fmt(cs.reduce((s, c) => s + num(c.monto_adeudado), 0))}</b>
                  </td>
                  {esMaster ? (
                    <td>
                      <button
                        className="btn sm"
                        disabled={crear.isPending || asignar.isPending}
                        onClick={() => void armarSugerido(clave, cs)}
                      >
                        Armar lote
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* --------- Crear y confirmar lotes (máster) --------- */}
      {esMaster ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <TituloCard>Lotes definidos</TituloCard>
          {catalogo.length === 0 ? (
            <p className="sub" style={{ marginBottom: 12 }}>
              Todavía no hay ningún lote definido: las {concesiones.data.length} concesiones figuran
              como lote individual.
            </p>
          ) : (
            <table style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Lote</th>
                  <th className="num">Concesiones</th>
                  <th className="num">Mínimo</th>
                  <th className="num">Garantía</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {catalogo.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <div className="nm">{l.codigo}</div>
                      {l.descripcion ? <div className="sub">{l.descripcion}</div> : null}
                    </td>
                    <td className="num">{l.concesiones}</td>
                    <td className="num">{l.concesiones ? fmt(l.minimo) : '—'}</td>
                    <td className="num">{l.concesiones ? fmt(l.garantia) : '—'}</td>
                    <td>
                      {l.concesiones === 0 ? (
                        <Tag clase="t-desc">Vacío — asignale concesiones</Tag>
                      ) : (
                        <Tag clase={l.confirmado ? 't-pagada' : 't-media'}>
                          {l.confirmado ? 'Confirmado con la publicación' : 'Hipótesis'}
                        </Tag>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn sm"
                        disabled={l.concesiones === 0}
                        onClick={() =>
                          confirmar
                            .mutateAsync({ loteId: l.id, confirmado: !l.confirmado })
                            .catch(avisar)
                        }
                      >
                        {l.confirmado ? 'Volver a hipótesis' : 'Confirmar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', gap: 9, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ marginBottom: 0, maxWidth: 240 }}>
              <label>Código del lote nuevo</label>
              <input
                value={nuevoCodigo}
                placeholder="Lote 1"
                onChange={(e) => setNuevoCodigo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void crearLote()}
              />
            </div>
            <button className="btn pri" disabled={crear.isPending} onClick={() => void crearLote()}>
              Crear lote
            </button>
            <span className="sub">
              Después asigna las concesiones desde el selector de cada grupo.
            </span>
          </div>
        </div>
      ) : null}

      {/* --------- Los grupos, como en el prototipo --------- */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(330px,1fr))' }}>
        {grupos.map(([etiqueta, cs]) => {
          const total = cs.reduce((s, c) => s + num(c.monto_adeudado), 0)
          const marcadas = cs.filter(
            (c) => c.estado === 'EN_REMATE' && (c.interes === 'ALTA' || c.interes === 'MEDIA'),
          ).length
          const enRemate = cs.filter((c) => c.estado === 'EN_REMATE').length

          return (
            <div className="card" key={etiqueta}>
              <div style={{ fontWeight: 600, marginBottom: 3 }}>{etiqueta}</div>
              <div className="sub" style={{ marginBottom: 10 }}>
                {plural(cs.length, 'concesión', 'concesiones')} · {cs[0].comuna}
                {enRemate < cs.length ? ` · ${cs.length - enRemate} fuera del remate` : ''}
                {marcadas > 0 ? ` · ${marcadas} marcada${marcadas > 1 ? 's' : ''}` : ''}
              </div>

              {cs.map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '5px 0',
                    borderBottom: '1px solid #1c242f',
                    fontSize: 12.5,
                    cursor: 'pointer',
                    opacity: c.estado === 'EN_REMATE' ? 1 : 0.5,
                  }}
                  onClick={() => setParams({ ficha: c.rol_nacional })}
                >
                  <span>{c.nombre}</span>
                  <span className="num sub">{fmt(c.monto_adeudado)}</span>
                </div>
              ))}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: 10,
                  fontWeight: 600,
                }}
              >
                <span>Mínimo del lote</span>
                <span>{fmt(total)}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: marcadas > 0 ? 'var(--acc2)' : 'var(--txt3)',
                }}
              >
                <span>Garantía a rendir (art. 148)</span>
                <span>{fmt(total)}</span>
              </div>

              {esMaster && catalogo.length > 0 ? (
                <select
                  style={{
                    marginTop: 10,
                    width: '100%',
                    background: 'var(--panel2)',
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    padding: '7px 9px',
                  }}
                  value={cs[0].lote_id ?? ''}
                  onChange={(e) => {
                    const loteId = e.target.value ? Number(e.target.value) : null
                    for (const c of cs) {
                      asignar.mutateAsync({ id: c.id, loteId }).catch(avisar)
                    }
                  }}
                >
                  <option value="">Sin lote (individual)</option>
                  {catalogo.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.codigo}
                      {l.concesiones === 0 ? ' (vacío)' : ''}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          )
        })}
      </div>
    </>
  )
}

/** Agrupa por lote, o por concesión individual si no tiene lote asignado. */
function agrupar(concesiones: Concesion[]): Array<[string, Concesion[]]> {
  const grupos = new Map<string, Concesion[]>()
  for (const c of concesiones) {
    const clave = c.lote ?? `Individual — ${c.rol_nacional}`
    const g = grupos.get(clave)
    if (g) g.push(c)
    else grupos.set(clave, [c])
  }
  return [...grupos.entries()].sort(
    (a, b) =>
      b[1].length - a[1].length ||
      b[1].reduce((s, c) => s + num(c.monto_adeudado), 0) -
        a[1].reduce((s, c) => s + num(c.monto_adeudado), 0),
  )
}
